import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { campaignInput, type Paginated } from '@barbeiropro/contracts';
import { Field } from '@/components/shared/field';
import { FormModal } from '@/components/shared/form-modal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/lib/api';
import { useBarbers } from '@/lib/org';
import { useSession } from '@/lib/session';
import type { Campaign, Coupon } from './api';
import { applyServerError, zodForm } from './lib';

interface Values {
  name: string;
  subject: string;
  body: string;
  inactiveDays: string;
  barberId: string;
  tags: string;
  couponId: string;
}
const NONE = 'none';
const empty: Values = { name: '', subject: '', body: '', inactiveDays: NONE, barberId: NONE, tags: '', couponId: NONE };
const toPayload = (v: Values) => ({
  name: v.name,
  subject: v.subject,
  body: v.body,
  audience: {
    inactiveDays: v.inactiveDays === NONE ? null : Number(v.inactiveDays),
    barberId: v.barberId === NONE ? null : v.barberId,
    tags: v.tags.split(',').map((t) => t.trim()).filter(Boolean),
  },
  couponId: v.couponId === NONE ? null : v.couponId,
});

export function CampaignFormModal({ open, onOpenChange, campaign }: { open: boolean; onOpenChange: (v: boolean) => void; campaign: Campaign | null }) {
  const qc = useQueryClient();
  const { can } = useSession();
  const barbers = useBarbers();
  const coupons = useQuery({
    queryKey: ['loyalty', 'coupons', 'options'],
    enabled: open && can('loyalty.manage'),
    queryFn: async () => (await api.get<Paginated<Coupon>>('/coupons', { pageSize: 100, active: 'true' })).data,
  });
  const form = useForm<Values>({ resolver: zodForm<Values>(campaignInput, toPayload), defaultValues: empty });
  const { errors } = form.formState;
  useEffect(() => {
    if (!open) return;
    form.reset(
      campaign
        ? {
            name: campaign.name,
            subject: campaign.subject,
            body: campaign.body,
            inactiveDays: campaign.audience.inactiveDays ? String(campaign.audience.inactiveDays) : NONE,
            barberId: campaign.audience.barberId ?? NONE,
            tags: (campaign.audience.tags ?? []).join(', '),
            couponId: campaign.couponId ?? NONE,
          }
        : empty,
    );
  }, [open, campaign]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = useMutation({
    mutationFn: (v: Values) => {
      const body = campaignInput.parse(toPayload(v));
      return campaign ? api.put(`/campaigns/${campaign.id}`, body) : api.post('/campaigns', body);
    },
    onSuccess: () => {
      toast.success('Campanha salva como rascunho');
      qc.invalidateQueries({ queryKey: ['campaigns'] });
      onOpenChange(false);
    },
    onError: (e) => applyServerError(form, e),
  });

  const select = (name: 'inactiveDays' | 'barberId' | 'couponId', id: string, items: { value: string; label: string }[]) => (
    <Controller
      control={form.control}
      name={name}
      render={({ field }) => (
        <Select value={field.value} onValueChange={field.onChange}>
          <SelectTrigger id={id} className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {items.map((i) => (
              <SelectItem key={i.value} value={i.value}>
                {i.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    />
  );

  return (
    <FormModal
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      title={campaign ? 'Editar campanha' : 'Nova campanha'}
      description="O e-mail só vai para clientes que aceitaram receber comunicações e têm e-mail cadastrado."
      footer={
        <>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Voltar
          </Button>
          <Button type="submit" form="campaign-form" disabled={save.isPending}>
            {save.isPending ? 'Salvando…' : 'Salvar rascunho'}
          </Button>
        </>
      }
    >
      <form id="campaign-form" className="grid gap-4" noValidate onSubmit={form.handleSubmit((v) => save.mutate(v))}>
        <Field id="cmp-name" label="Nome interno" error={errors.name?.message}>
          <Input id="cmp-name" autoFocus {...form.register('name')} />
        </Field>
        <Field id="cmp-subject" label="Assunto do e-mail" error={errors.subject?.message}>
          <Input id="cmp-subject" maxLength={150} {...form.register('subject')} />
        </Field>
        <Field id="cmp-body" label="Mensagem" hint="Use {nome} para inserir o primeiro nome do cliente. Separe parágrafos com uma linha em branco." error={errors.body?.message}>
          <Textarea id="cmp-body" rows={6} maxLength={5000} {...form.register('body')} />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field id="cmp-inactive" label="Inatividade">
            {select('inactiveDays', 'cmp-inactive', [
              { value: NONE, label: 'Todos os clientes' },
              ...[30, 45, 60, 90].map((n) => ({ value: String(n), label: `Sem vir há ${n} dias ou mais` })),
            ])}
          </Field>
          <Field id="cmp-barber" label="Barbeiro preferido">
            {select('barberId', 'cmp-barber', [{ value: NONE, label: 'Qualquer um' }, ...(barbers.data ?? []).map((b) => ({ value: b.id, label: b.displayName }))])}
          </Field>
          <Field id="cmp-tags" label="Etiquetas (opcional)" hint="Separe por vírgula. Basta o cliente ter uma delas.">
            <Input id="cmp-tags" {...form.register('tags')} />
          </Field>
          {can('loyalty.manage') && (
            <Field id="cmp-coupon" label="Cupom incluído (opcional)">
              {select('couponId', 'cmp-coupon', [{ value: NONE, label: 'Sem cupom' }, ...(coupons.data ?? []).map((c) => ({ value: c.id, label: c.code }))])}
            </Field>
          )}
        </div>
      </form>
    </FormModal>
  );
}
